'use client';

import React, { ComponentType, useCallback, useRef, useState } from 'react';
import clsx from 'clsx';
import { Swiper as SwiperRoot, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import { StepsIndicator } from '@/components/step-indicator/StepsIndicator';
import { indicatorVariantByTheme, navButtonBase, themeStyles } from './const';
import type { SwiperProps, SwiperVariant, VariantSlideProps } from './types';
import { PrimarySlide } from './variants/PrimarySlide';
import Icon from '@/components/icon/Icon';
import { Headline } from '../headline/Headline';
import SerializeDescription from '../serialize-description/SerializeDescription';
import { Link } from '../link/Link';

const variantComponents: Record<
  SwiperVariant,
  ComponentType<VariantSlideProps>
> = {
  primary: PrimarySlide,
};

const Swiper = ({
  slides = [],
  variant = 'primary',
  theme = 'light',
  showPagination = true,
  className,
  ariaLabel = 'Slider Navigation',
  headline,
  description,
  link,
}: SwiperProps) => {
  const total = slides?.length;
  const [current, setCurrent] = useState(0);
  const instanceRef = useRef<SwiperType | null>(null);
  const styles = themeStyles[theme];
  const isFirst = current === 0;
  const isLast = current === total - 1;

  const handleNext = useCallback(() => {
    instanceRef.current?.slideNext();
  }, []);

  const handlePrev = useCallback(() => {
    instanceRef.current?.slidePrev();
  }, []);

  if (total === 0) {
    return null;
  }

  const SlideComponent = variantComponents[variant] ?? PrimarySlide;
  const indicator = (
    <StepsIndicator
      totalSteps={total}
      currentStep={current + 1}
      variant={indicatorVariantByTheme[theme]}
      type="step"
      showLabel={false}
      align="center"
      ariaLabel={ariaLabel}
    />
  );

  return (
    <div className={clsx('w-full py-10', styles.background, className)}>
      <div
        className={clsx(
          'container mx-auto flex w-full flex-col items-center justify-center text-center lg:w-2/4',
          styles.container,
        )}
      >
        {headline && (
          <Headline
            {...headline}
            className={clsx(styles.heading, headline.className)}
          />
        )}
        {description?.content && (
          <SerializeDescription
            description={description.content}
            className={clsx(
              theme === 'dark' &&
                'text-white [&_*]:text-white [&_a]:text-white',
              'mb-4',
            )}
          />
        )}
        {link && <Link {...link} />}
      </div>
      <div className="container relative mx-auto pt-10 lg:pt-0">
        <SwiperRoot
          slidesPerView={1}
          spaceBetween={12}
          autoHeight
          onSwiper={(swiper) => {
            instanceRef.current = swiper;
          }}
          onSlideChange={(swiper) => setCurrent(swiper.activeIndex)}
          allowTouchMove={total > 1}
        >
          {slides?.map((item, index) => (
            <SwiperSlide key={item.id ?? index} className="!h-auto">
              <SlideComponent
                item={item}
                theme={theme}
                index={index}
                total={total}
              />
            </SwiperSlide>
          ))}
        </SwiperRoot>

        {/* Desktop navigation overlays */}
        <div className="pointer-events-none absolute inset-y-0 left-0 right-0 z-10 hidden items-center justify-between lg:flex">
          <button
            type="button"
            aria-label="Vorheriger Slide"
            onClick={handlePrev}
            disabled={total <= 1 || isFirst}
            className={clsx(
              navButtonBase,
              styles.nav,
              styles.focus,
              'pointer-events-auto shadow-lg',
            )}
          >
            <Icon name="arrow-left" />
          </button>

          <button
            type="button"
            aria-label="Nächster Slide"
            onClick={handleNext}
            disabled={total <= 1 || isLast}
            className={clsx(
              navButtonBase,
              styles.nav,
              styles.focus,
              'pointer-events-auto shadow-lg',
            )}
          >
            <Icon name="arrow-right" />
          </button>
        </div>

        {showPagination && (
          <div>
            <div className="hidden justify-center lg:flex">{indicator}</div>
            {/* Mobile stack: arrows on top, indicator below */}
            <div className="flex flex-col items-center justify-between gap-3 lg:hidden">
              <div className="mb-3 flex w-full items-center justify-between">
                <button
                  type="button"
                  aria-label="Vorheriger Slide"
                  onClick={handlePrev}
                  disabled={total <= 1 || isFirst}
                  className={clsx(
                    navButtonBase,
                    styles.nav,
                    styles.focus,
                    'h-10 w-10 shadow-md',
                  )}
                >
                  <Icon name="arrow-left" />
                </button>
                <button
                  type="button"
                  aria-label="Nächster Slide"
                  onClick={handleNext}
                  disabled={total <= 1 || isLast}
                  className={clsx(
                    navButtonBase,
                    styles.nav,
                    styles.focus,
                    'h-10 w-10 shadow-md',
                  )}
                >
                  <Icon name="arrow-right" />
                </button>
              </div>
              <div className="w-full">{indicator}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Swiper;
